<template>
  <div class="cover-art-generator">
    <h1 class="bold">
      Cover Art Generator 
    </h1>

    <p v-if="isFetching" class="loading-message">
      <span class="spinner" /> Fetching data...
    </p>

    <div v-else>
      <div class="prompts-section">
        <h2 class="section-title">Configure AI Prompts</h2>
        <p class="section-description">Customize the prompts used to generate different styles of cover art</p>
        
        <form @submit.prevent="handleSavePrompts" class="prompts-form">
          <div class="prompts-grid">
            <div class="prompt-group">
              <BaseInput 
                name="promptCustom" 
                label="✨ Custom Prompt" 
                :disabled="isSaving" 
                placeholder="Provide custom prompt" 
                type="textarea" 
                :value="promptCustom"
                @input="promptCustom = $event.value"
              />
            </div>

            <div class="prompt-group">
              <BaseInput 
                name="promptModernSleek" 
                label="🎯 Modern & Sleek Prompt" 
                :disabled="isSaving" 
                placeholder="Prompt for modern & sleek cover art" 
                type="textarea" 
                :value="promptModernSleek"
                @input="promptModernSleek = $event.value"
              />
            </div>

            <div class="prompt-group">
              <BaseInput 
                name="promptAbstract" 
                label="🎨 Abstract Prompt" 
                :disabled="isSaving" 
                placeholder="Prompt for abstract cover art" 
                type="textarea" 
                :value="promptAbstract"
                @input="promptAbstract = $event.value"
              />
            </div>

            <div class="prompt-group">
              <BaseInput 
                name="promptMinimalistic" 
                label="✨ Minimalistic Prompt" 
                :disabled="isSaving" 
                placeholder="Prompt for minimalistic cover art" 
                type="textarea" 
                :value="promptMinimalistic"
                @input="promptMinimalistic = $event.value"
              />
            </div>
          </div>

          <div class="form-actions">
            <button class="purple-btn save-btn" 
                    :disabled="isSaving" 
                    type="submit">
                <span class="spinner" v-if="isSaving"/>
                <span class="btn-text">{{isSaving ? 'Saving...' : 'Save All Prompts'}}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
  import BaseInput from "@/components/BaseInput.vue";
  import {computed, ref} from "vue";
  import {useStore} from "vuex";

  const { state, dispatch, commit } = useStore()

  dispatch('get_cover_art_prompts')

  const isFetching = computed(() => state.reqStatus === "fetching");
  const isSaving = computed(() => state.reqStatus === "sending");

  // Prompt Custom
  const _promptCustom = ref('')
  const promptCustom = computed({
    get: () => _promptCustom.value || state.coverArtGenerator.prompts?.promptCoverArtCustom || '',
    set: (value) => {
      _promptCustom.value = value
      commit('SET_COVER_ART_STATE', {key: 'prompts', value: {...state.coverArtGenerator.prompts, promptCoverArtCustom: value}})
    }
  })

  // Prompt Modern & Sleek
  const _promptModernSleek = ref('')
  const promptModernSleek = computed({
    get: () => _promptModernSleek.value || state.coverArtGenerator.prompts?.promptCoverArtModernSleek || '',
    set: (value) => {
      _promptModernSleek.value = value
      commit('SET_COVER_ART_STATE', {key: 'prompts', value: {...state.coverArtGenerator.prompts, promptCoverArtModernSleek: value}})
    }
  })

  // Prompt Abstract
  const _promptAbstract = ref('')
  const promptAbstract = computed({
    get: () => _promptAbstract.value || state.coverArtGenerator.prompts?.promptCoverArtAbstract || '',
    set: (value) => {
      _promptAbstract.value = value
      commit('SET_COVER_ART_STATE', {key: 'prompts', value: {...state.coverArtGenerator.prompts, promptCoverArtAbstract: value}})
    }
  })

  // Prompt Minimalistic
  const _promptMinimalistic = ref('')
  const promptMinimalistic = computed({
    get: () => _promptMinimalistic.value || state.coverArtGenerator.prompts?.promptCoverArtMinimalistic || '',
    set: (value) => {
      _promptMinimalistic.value = value
      commit('SET_COVER_ART_STATE', {key: 'prompts', value: {...state.coverArtGenerator.prompts, promptCoverArtMinimalistic: value}})
    }
  })

  function handleSavePrompts() {
    dispatch('update_cover_art_prompts')
  }
</script>

<style lang="scss">
  .cover-art-generator {
    @media only screen and (min-width: 0) {
      padding-bottom: 16px;

      .prompts-section,
      .generation-section {
        background-color: #fff;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        padding: 32px;
        margin-bottom: 32px;
        border: 1px solid #e2e8f0;
      }

      .prompts-form {
        .prompts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
          gap: 24px;
          margin-bottom: 32px;

          @media (max-width: 768px) {
            grid-template-columns: 1fr;
          }
        }

        .prompt-group {
          background-color: #f8fafc;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          transition: border-color 0.2s ease;

          &:hover {
            border-color: #cbd5e0;
          }

          textarea {
            min-height: 120px;
            font-size: 14px;
            line-height: 1.5;
          }
        }
      }


      .form-actions {
        display: flex;
        justify-content: flex-end;
        padding-top: 16px;
        border-top: 1px solid #e2e8f0;

        .purple-btn {
          padding: 8px 16px;
          min-width: 160px;
          height: 48px;
          font-size: 16px;
          font-weight: 600;
          border-radius: 8px;
          transition: all 0.2s ease;
          display: flex;
          justify-content: center;
          align-items: center;

          &.save-btn {
            background: #7e3af2;
          }

          &.generate-btn {
            background: #7e3af2;
          }

          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 6px 12px rgba(126, 58, 242, 0.3);
          }
        }
      }

      .loading-message {
        display: flex;
        align-items: center;
        font-size: 16px;
        color: #666;
        justify-content: center;
        padding: 40px;
        background-color: #f8fafc;
        border-radius: 8px;
        margin: 20px 0;
      }

      .spinner {
        display: inline-block;
        width: 20px;
        height: 20px;
        margin-right: 10px;
        border-radius: 50%;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: #fff;
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      .section-title {
        font-size: 24px;
        font-weight: 700;
        margin-bottom: 8px;
        color: #1a202c;
        position: relative;
        
        &:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -8px;
          width: 50px;
          height: 4px;
          background: linear-gradient(45deg, #7e3af2, #a855f7);
          border-radius: 2px;
        }
      }

      .section-description {
        font-size: 16px;
        color: #64748b;
        margin-bottom: 24px;
        line-height: 1.6;
      }
    }
  }
</style>